/* JEFE Build Your Bundle — tier + customer-selected subscription cadence (2026-09-27). */
(() => {
  'use strict';
  if (customElements.get('jefe-product-offer')) return;

  const readJSON = (root, selector) => {
    const node = root.querySelector(selector);
    if (!node) throw new Error(`Missing JEFE bundle data: ${selector}`);
    return JSON.parse(node.textContent);
  };
  const int = (value, fallback = 0) => {
    const n = Number(value);
    return Number.isFinite(n) ? Math.trunc(n) : fallback;
  };
  // Only write changed values. Rewriting equal text/attributes still emits mutations.
  const setText = (node, value) => {
    const text = String(value ?? '');
    if (node && node.textContent !== text) node.textContent = text;
  };
  const setHidden = (node, hidden) => {
    if (node && node.hidden !== Boolean(hidden)) node.hidden = Boolean(hidden);
  };

  class JefeProductOffer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      try { this.initialize(); }
      catch (error) {
        console.error('[JEFE bundle]', error);
        const message = this.querySelector('[data-jefe-error]');
        if (message) { message.textContent = 'Please reload this page.'; message.hidden = false; }
      }
    }

    initialize() {
      this.config = readJSON(this, '[data-jefe-config]');
      this.variants = new Map((this.config.variants || []).map((variant) => [String(variant.id), variant]));
      this.quantities = this.quantities || new Map();
      this.mode = this.mode || (this.config.defaultPurchase === 'onetime' ? 'onetime' : 'subscription');
      this.selectedFrequencyDays = Number(this.selectedFrequencyDays) === 60 ? 60 : 30;
      this.frequencyEligible = false;
      this.section = this.closest('product-info') || this.closest('.shopify-section') || this.parentElement;
      this.section?.setAttribute('data-jefe-offer-active', '');
      this.rows = Array.from(this.querySelectorAll('[data-jefe-flavor-row]'));
      this.purchaseButtons = Array.from(this.querySelectorAll('[data-jefe-purchase]'));
      this.form = null;
      this.button = null;
      this.loading = false;
      this.placedRisk = null;
      this.buttonSyncFrame = null;
      this.formSyncFrame = null;
      this.stickyVisibilityFrame = null;
      this.events?.abort();
      this.events = new AbortController();
      const listenerOptions = { signal: this.events.signal };
      // The offer itself is a CSS container (`container-type:inline-size`). A fixed
      // descendant of a containment context can become fixed to that container on
      // mobile Safari instead of to the viewport. Move the sticky bar to <body>
      // so it is genuinely viewport-fixed while keeping all bundle state here.
      this.mobileSticky = this.querySelector('[data-jefe-mobile-sticky]');
      if (this.mobileSticky) {
        const computed = getComputedStyle(this);
        ['--jefe-button','--jefe-button-text','--jefe-disabled','--jefe-disabled-text'].forEach((name) => {
          const value = computed.getPropertyValue(name).trim();
          if (value) this.mobileSticky.style.setProperty(name, value);
        });
        this.mobileSticky.dataset.jefeStickyPortal = 'true';
        document.body.appendChild(this.mobileSticky);
      }
      this.stickyButton = this.mobileSticky?.querySelector('[data-jefe-sticky-atc]') || null;
      this.stickyLabel = this.mobileSticky?.querySelector('[data-jefe-sticky-label]') || null;
      this.stickyButton?.addEventListener('click', (event) => {
        event.preventDefault();
        if (!this.loading && !this.stickyButton.disabled) this.submitBundle(event);
      }, listenerOptions);

      try {
        this.moneyFormatter = new Intl.NumberFormat(this.config.locale || document.documentElement.lang || 'en', {
          style: 'currency', currency: this.config.currency || 'USD', currencyDisplay: 'symbol', minimumFractionDigits: 2, maximumFractionDigits: 2,
        });
      } catch (_) {
        this.moneyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: this.config.currency || 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }

      this.rows.forEach((row) => {
        const id = String(row.dataset.variantId || '');
        if (!this.quantities.has(id)) this.quantities.set(id, 0);
        row.querySelector('[data-jefe-add]')?.addEventListener('click', () => this.changeQuantity(id, 1), listenerOptions);
        row.querySelector('[data-jefe-inc]')?.addEventListener('click', () => this.changeQuantity(id, 1), listenerOptions);
        row.querySelector('[data-jefe-dec]')?.addEventListener('click', () => this.changeQuantity(id, -1), listenerOptions);
      });
      this.purchaseButtons.forEach((button) => button.addEventListener('click', () => {
        this.mode = button.dataset.jefePurchase === 'onetime' ? 'onetime' : 'subscription';
        this.update(true);
      }, listenerOptions));
      this.frequencySelect = this.querySelector('[data-jefe-frequency]');
      this.frequencySelect?.addEventListener('change', (event) => {
        const value = Number(event.currentTarget.value);
        if (value !== 30 && value !== 60) return;
        this.selectedFrequencyDays = value;
        this.mode = 'subscription';
        this.update(true);
      }, listenerOptions);

      // Section replacements in the editor may insert a new form or submit button.
      // Ignore price/text changes inside this bundle instead of scanning on every mutation.
      this.formObserver = new MutationObserver((records) => {
        if (!this.isConnected) return;
        const formChanged = !this.form?.isConnected || !this.button?.isConnected || records.some((record) => {
          if (this.contains(record.target) || this.button?.contains(record.target)) return false;
          return [...record.addedNodes, ...record.removedNodes].some((node) =>
            node.nodeType === 1 && (node.matches('form, product-form, button[type="submit"], input[type="submit"]') ||
              node.querySelector('form, product-form, button[type="submit"], input[type="submit"]'))
          );
        });
        if (formChanged) this.scheduleFormSync();
      });
      if (this.section) this.formObserver.observe(this.section, { childList: true, subtree: true });
      this.attachForm(this.findForm());
      this.initialized = true;
      this.registerTierPlans();
      this.initializeFlavorListSizing(listenerOptions);
      this.initializeMobileSticky();
      this.update(false);
      this.scheduleStickyVisibility();
      this.setAttribute('data-ready', 'true');
    }

    disconnectedCallback() {
      this.flavorRowsObserver?.disconnect();
      cancelAnimationFrame(this.flavorSizingFrame);
      this.flavorSizingFrame = null;
      cancelAnimationFrame(this.stickyVisibilityFrame);
      this.stickyVisibilityFrame = null;
      this.formObserver?.disconnect();
      this.events?.abort();
      cancelAnimationFrame(this.formSyncFrame);
      this.formSyncFrame = null;
      this.detachForm();
      this.placedRisk?.remove();
      const stickyPortal = this.mobileSticky;
      // Theme-editor section swaps create a fresh offer. Remove the old portal after
      // the current task only if this offer really stayed disconnected.
      setTimeout(() => {
        if (!this.isConnected && stickyPortal?.dataset.jefeStickyPortal === 'true') stickyPortal.remove();
      }, 0);
      if (!this.section?.querySelector('jefe-product-offer')) this.section?.removeAttribute('data-jefe-offer-active');
      this.initialized = false;
    }

    initializeFlavorListSizing(listenerOptions) {
      this.flavorList = this.querySelector('.jefe-bundle__flavor-list');
      this.flavorSizingFrame = null;
      this.flavorRowsObserver?.disconnect();
      if (!this.flavorList) return;
      const requestedRows = Number.parseInt(this.flavorList.dataset.visibleRows || '6', 10);
      this.visibleFlavorRows = Math.max(1, Math.min(12, Number.isFinite(requestedRows) ? requestedRows : 6));
      this.flavorList.classList.toggle('is-scrollable', this.rows.length > this.visibleFlavorRows);
      if (this.rows.length <= this.visibleFlavorRows) {
        this.flavorList.style.removeProperty('--jefe-visible-rows-height');
        return;
      }

      const resize = () => this.scheduleFlavorListSizing();
      // Observe row boxes, NOT the list height we write. This stays idle after
      // layout settles and avoids the observer feedback loop fixed previously.
      if (typeof ResizeObserver === 'function') {
        this.flavorRowsObserver = new ResizeObserver(resize);
        this.rows.slice(0, this.visibleFlavorRows).forEach((row) => this.flavorRowsObserver.observe(row));
      }
      window.addEventListener('resize', resize, listenerOptions);
      this.flavorList.addEventListener('load', resize, { ...listenerOptions, capture: true });
      // Font loading can change wrapped descriptions in older browsers too.
      document.fonts?.ready.then(() => {
        if (this.isConnected && !listenerOptions.signal.aborted) resize();
      });
      this.scheduleFlavorListSizing();
    }

    scheduleFlavorListSizing() {
      if (!this.isConnected || !this.flavorList || this.flavorSizingFrame !== null) return;
      this.flavorSizingFrame = requestAnimationFrame(() => {
        this.flavorSizingFrame = null;
        const visibleRows = this.visibleFlavorRows || 6;
        if (!this.isConnected || this.rows.length <= visibleRows) return;
        const first = this.rows[0].getBoundingClientRect();
        const lastVisible = this.rows[visibleRows - 1].getBoundingClientRect();
        // The difference is independent of the list's current scroll position.
        // It includes actual row padding, borders and multi-line descriptions.
        if (!first.width || lastVisible.bottom <= first.top) return;
        const height = Math.ceil((lastVisible.bottom - first.top) * 100) / 100;
        const value = `${height}px`;
        if (this.flavorList.style.getPropertyValue('--jefe-visible-rows-height') !== value) {
          this.flavorList.style.setProperty('--jefe-visible-rows-height', value);
        }
      });
    }

    initializeMobileSticky() {
      if (!this.mobileSticky) return;
      const schedule = () => this.scheduleStickyVisibility();
      window.addEventListener('scroll', schedule, { passive: true, signal: this.events.signal });
      window.addEventListener('resize', schedule, { passive: true, signal: this.events.signal });
      window.addEventListener('orientationchange', schedule, { passive: true, signal: this.events.signal });
      setHidden(this.mobileSticky, true);
    }

    scheduleStickyVisibility() {
      if (!this.mobileSticky || this.stickyVisibilityFrame !== null) return;
      this.stickyVisibilityFrame = requestAnimationFrame(() => {
        this.stickyVisibilityFrame = null;
        this.updateStickyVisibility();
      });
    }

    updateStickyVisibility() {
      if (!this.mobileSticky) return;
      if (window.matchMedia('(min-width: 750px)').matches) {
        setHidden(this.mobileSticky, true);
        return;
      }
      // The fifth flavor is the activation point. Once it reaches the viewport,
      // keep the bar visible until the normal product Add to Cart reaches view.
      const triggerRow = this.rows[4];
      const nativeButton = this.button;
      if (!triggerRow || !nativeButton?.isConnected) {
        setHidden(this.mobileSticky, true);
        return;
      }
      const viewportHeight = window.visualViewport?.height || window.innerHeight || document.documentElement.clientHeight;
      const triggerRect = triggerRow.getBoundingClientRect();
      const nativeRect = nativeButton.getBoundingClientRect();
      const fifthFlavorReached = triggerRect.top <= viewportHeight;
      const nativeButtonStillBelowViewport = nativeRect.top > viewportHeight;
      setHidden(this.mobileSticky, !(fifthFlavorReached && nativeButtonStillBelowViewport));
    }

    syncStickyButton() {
      if (!this.stickyButton) return;
      const nativeLabel = this.button?.querySelector(':scope > span:not(.loading__spinner)');
      const disabled = !this.button || this.button.disabled || this.loading;
      this.stickyButton.disabled = disabled;
      this.stickyButton.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      this.stickyButton.classList.toggle('loading', Boolean(this.loading));
      if (nativeLabel && this.stickyLabel && this.stickyLabel.innerHTML !== nativeLabel.innerHTML) {
        this.stickyLabel.innerHTML = nativeLabel.innerHTML;
      }
      this.scheduleStickyVisibility();
    }

    money(cents) { return this.moneyFormatter.format(Math.max(0, Math.round(Number(cents) || 0)) / 100); }
    totalBags() { return Array.from(this.quantities.values()).reduce((sum, quantity) => sum + quantity, 0); }
    tier(total = this.totalBags()) { return total <= 1 ? 1 : total === 2 ? 2 : total === 3 ? 3 : 4; }
    price(mode, tier = this.tier()) {
      const table = mode === 'onetime' ? this.config.otpPrices : this.config.subPrices;
      return Math.max(0, int(table?.[String(tier)], 0));
    }
    savings(tier = this.tier()) {
      const otp = this.price('onetime', tier);
      const sub = this.price('subscription', tier);
      if (!otp || sub >= otp) return 0;
      return Math.max(0, Math.round((otp - sub) * 100 / otp));
    }
    deliveryDaysForTier() {
      return Number(this.selectedFrequencyDays) === 60 ? 60 : 30;
    }

    quantityAllowed(variant, quantity) {
      if (!variant?.available || quantity < 0) return false;
      if (quantity === 0) return true;
      const stock = variant.stockLimit == null ? Infinity : Math.max(0, Number(variant.stockLimit) || 0);
      if (quantity > stock) return false;
      const rule = variant.quantityRule || {};
      const minimum = Math.max(1, int(rule.min, 1));
      const maximum = rule.max == null ? Infinity : Math.max(minimum, int(rule.max, minimum));
      const increment = Math.max(1, int(rule.increment, 1));
      return quantity >= minimum && quantity <= maximum && ((quantity - minimum) % increment === 0);
    }

    changeQuantity(id, delta) {
      const variant = this.variants.get(String(id));
      const current = this.quantities.get(String(id)) || 0;
      const next = Math.max(0, current + delta);
      if (delta > 0 && !this.quantityAllowed(variant, next)) {
        this.showError(this.config.quantityError || 'That quantity is unavailable.');
        return;
      }
      this.quantities.set(String(id), next);
      if (delta > 0) this.showVariantMedia(variant);
      this.showError('');
      this.update(true);
    }

    showVariantMedia(variant) {
      const mediaId = String(variant?.featuredMediaId || '');
      if (!mediaId) return;
      const gallery = this.section?.querySelector('media-gallery');
      if (!gallery?.setActiveMedia) return;
      const mediaItem = Array.from(gallery.querySelectorAll('[data-media-id]')).find((item) =>
        String(item.dataset.mediaId || '').endsWith(`-${mediaId}`)
      );
      if (!mediaItem) return;
      gallery.setActiveMedia(mediaItem.dataset.mediaId, false);
    }

    planText(plan) {
      return [plan?.name || '', ...(plan?.options || []).flatMap((option) => [option?.name || '', option?.value || ''])].join(' ').toLowerCase();
    }
    planDays(plan) {
      const source = this.planText(plan);
      const day = source.match(/(?:every|each|per)?\s*(\d+)\s*days?\b/);
      if (day) return Number(day[1]);
      const month = source.match(/(?:every|each|per)?\s*(\d+)\s*months?\b/);
      if (month) return Number(month[1]) * 30;
      if (/\bmonthly\b|\bevery\s+month\b|\beach\s+month\b|\bper\s+month\b/.test(source)) return 30;
      return null;
    }
    planForVariant(variant, total = this.totalBags(), frequencyDays = null) {
      const plans = variant?.plans || [];
      if (!plans.length) return null;

      const tier = this.tier(total);
      const desiredDays = Number(frequencyDays) || this.deliveryDaysForTier(tier);
      const desiredPrice = this.price('subscription', tier);
      const planPrice = (plan) => {
        const value = plan?.perDeliveryPrice ?? plan?.price;
        return Number.isFinite(Number(value)) ? Math.round(Number(value)) : null;
      };

      // Multiple tiers can share a cadence, and every tier can use either
      // monthly or every-two-month delivery. Match BOTH the real Shopify
      // allocation price and cadence so the cart receives the exact plan.
      const exact = plans.find((plan) => this.planDays(plan) === desiredDays && planPrice(plan) === desiredPrice);
      if (exact) return exact;

      // Never fall back to cadence-only or to the first plan. Different bundle
      // tiers can share the same cadence, so guessing can silently create the
      // wrong recurring charge. If Shopify does not expose an exact price +
      // cadence match, blockedReason() surfaces the setup error instead.
      return null;
    }

    registerTierPlans() {
      const registry = { ...(window.JefeTierPlanRegistry || {}) };
      this.variants.forEach((variant, id) => {
        const map = {};
        for (let step = 1; step <= 4; step += 1) {
          const daysList = [30, 60];
          const cadenceMap = {};
          daysList.forEach((days) => {
            const plan = this.planForVariant(variant, step, days);
            if (plan?.id) cadenceMap[String(days)] = String(plan.id);
          });
          if (Object.keys(cadenceMap).length) map[String(step)] = cadenceMap;
        }
        if (Object.keys(map).length) registry[String(id)] = map;
      });
      window.JefeTierPlanRegistry = registry;
      try { sessionStorage.setItem('jefe:tier-plan-registry', JSON.stringify(registry)); } catch (_) {}
    }

    selectedEntries() {
      const result = [];
      this.quantities.forEach((quantity, id) => {
        if (quantity > 0) result.push({ id, quantity, variant: this.variants.get(id) });
      });
      return result;
    }

    blockedReason() {
      const entries = this.selectedEntries();
      if (!entries.length) return this.config.emptyButtonLabel || 'Select at least 1 bag';
      for (const entry of entries) {
        if (!this.quantityAllowed(entry.variant, entry.quantity)) return this.config.quantityError || 'That quantity is unavailable.';
        if (this.mode === 'subscription' && !this.planForVariant(entry.variant)) return this.config.subscriptionError || 'Subscription is unavailable for one or more selected flavors.';
      }
      return '';
    }

    showError(text = '') {
      const node = this.querySelector('[data-jefe-error]');
      setText(node, text);
      setHidden(node, !text);
    }

    update(announce = false) {
      const total = this.totalBags();
      const tier = this.tier(total);
      // Monthly and every-2-month delivery are available at every bundle tier.
      // Keep the selector visible even before a bag is chosen so the subscription
      // card keeps the same full layout whether subscription or OTP is selected.
      const eligibleForFrequency = true;
      if (!this.frequencyEligible) this.selectedFrequencyDays = 30;
      this.frequencyEligible = true;
      const deliveryDays = this.deliveryDaysForTier(tier);
      const saving = this.savings(tier);
      setText(this.querySelector('[data-jefe-bag-count]'), total);

      this.rows.forEach((row) => {
        const id = String(row.dataset.variantId || '');
        const quantity = this.quantities.get(id) || 0;
        setText(row.querySelector('[data-jefe-qty]'), quantity);
        setHidden(row.querySelector('[data-jefe-qty-control]'), quantity === 0);
        setHidden(row.querySelector('[data-jefe-add]'), quantity > 0);
      });

      // Quantity is counted across all selected flavor variants, not gift products.
      // Track starts at milestone 1 and ends at milestone 4 (three equal segments).
      const reached = Math.min(4, Math.max(0, total));
      const fill = this.querySelector('[data-jefe-progress-fill]');
      if (fill) fill.style.width = `${Math.max(0, reached - 1) / 3 * 100}%`;
      const meter = this.querySelector('[data-jefe-progress-meter]');
      if (meter) {
        meter.setAttribute('aria-valuenow', String(reached));
        meter.setAttribute('aria-valuetext', `${total} ${total === 1 ? 'bag' : 'bags'} selected${total >= 4 ? '. All rewards unlocked.' : ''}`);
      }
      this.querySelectorAll('[data-jefe-dot]').forEach((dot) => {
        const step = Number(dot.dataset.jefeDot);
        dot.classList.toggle('is-complete', total > 0 && step <= reached);
        dot.classList.toggle('is-current', total > 0 && step === reached);
      });
      this.querySelectorAll('[data-jefe-reward]').forEach((reward) => {
        const threshold = Number(reward.dataset.jefeReward);
        const unlocked = total >= threshold;
        reward.classList.toggle('is-unlocked', unlocked);
        reward.dataset.locked = String(!unlocked);
        setText(reward.querySelector('[data-jefe-reward-status]'), unlocked ? 'Unlocked.' : `Unlocks at ${threshold} bags.`);
      });

      for (let step = 1; step <= 4; step += 1) {
        const node = this.querySelector(`[data-jefe-tier-price="${step}"]`);
        setText(node, `${this.money(this.price(this.mode, step))}/ea`);
        node?.classList.toggle('is-current', total > 0 && step === tier);
      }

      this.querySelectorAll('[data-jefe-purchase-card]').forEach((card) => card.classList.toggle('is-selected', card.dataset.jefePurchaseCard === this.mode));
      this.purchaseButtons.forEach((button) => button.setAttribute('aria-pressed', button.dataset.jefePurchase === this.mode ? 'true' : 'false'));
      setText(this.querySelector('[data-jefe-sub-ea]'), this.money(this.price('subscription', tier)));
      setText(this.querySelector('[data-jefe-otp-ea]'), this.money(this.price('onetime', tier)));
      const cadenceNode = this.querySelector('[data-jefe-cadence]');
      const frequencyControl = this.querySelector('[data-jefe-frequency-control]');
      setHidden(cadenceNode, eligibleForFrequency);
      setHidden(frequencyControl, !eligibleForFrequency);
      if (!eligibleForFrequency) setText(cadenceNode, this.config.cadenceLabels?.['1'] || 'Deliver every month');
      if (this.frequencySelect && this.frequencySelect.value !== String(deliveryDays)) this.frequencySelect.value = String(deliveryDays);
      setText(this.querySelector('[data-jefe-save-banner]'), `SAVE ${saving}% ON FIRST ORDER`);
      this.querySelectorAll('[data-jefe-benefit-template]').forEach((node) => {
        setText(node, String(node.dataset.jefeBenefitTemplate || '').replace(/\[saving\]/gi, String(saving)));
      });

      this.syncButton();
      const reason = this.blockedReason();
      if (reason && total > 0) this.showError(reason); else if (!this.loading) this.showError('');
      if (announce) {
        const price = this.price(this.mode, tier);
        setText(this.querySelector('[data-jefe-announcement]'), total ? `${total} bags selected. ${this.money(price)} each.` : (this.config.emptyButtonLabel || 'Select at least 1 bag'));
      }
    }

    findForm() {
      // Scope the lookup: theme-editor transitions can briefly duplicate DOM ids.
      return Array.from(this.section?.querySelectorAll('form') || [])
        .find((form) => form.getAttribute('id') === this.dataset.formId) || null;
    }

    scheduleFormSync() {
      if (this.formSyncFrame !== null) return;
      this.formSyncFrame = requestAnimationFrame(() => {
        this.formSyncFrame = null;
        if (this.isConnected) this.attachForm(this.findForm());
      });
    }

    scheduleButtonSync() {
      if (!this.isConnected || this.buttonSyncFrame !== null) return;
      this.buttonSyncFrame = requestAnimationFrame(() => {
        this.buttonSyncFrame = null;
        if (this.isConnected) this.syncButton();
      });
    }

    observeButton() {
      if (!this.isConnected || !this.button?.isConnected) return;
      this.buttonObserver?.observe(this.button, {
        attributes: true, childList: true, characterData: true, subtree: true,
        attributeFilter: ['disabled', 'aria-disabled'],
      });
    }

    detachForm() {
      if (this.form && this.onSubmitBound) this.form.removeEventListener('submit', this.onSubmitBound, true);
      this.buttonObserver?.disconnect();
      cancelAnimationFrame(this.buttonSyncFrame);
      this.buttonSyncFrame = null;
      this.form?.classList.remove('jefe-offer-form');
      this.form = null;
      this.button = null;
    }

    attachForm(form) {
      if (!form || !this.section?.contains(form)) {
        if (this.form && !this.form.isConnected) {
          this.detachForm();
          this.placedRisk?.remove();
          this.placedRisk = null;
        }
        return;
      }
      const button = form.querySelector('[type="submit"][name="add"]') || form.querySelector('[type="submit"]');
      if (form === this.form && button === this.button) return;
      this.detachForm();
      this.form = form;
      this.form.classList.add('jefe-offer-form');
      this.form.style.setProperty('--jefe-button-bg', getComputedStyle(this).getPropertyValue('--jefe-button').trim() || '#ffd900');
      this.form.style.setProperty('--jefe-button-text', getComputedStyle(this).getPropertyValue('--jefe-button-text').trim() || '#111');
      this.form.style.setProperty('--jefe-disabled-bg', getComputedStyle(this).getPropertyValue('--jefe-disabled').trim() || '#dedede');
      this.form.style.setProperty('--jefe-disabled-text', getComputedStyle(this).getPropertyValue('--jefe-disabled-text').trim() || '#999');
      this.button = button;
      this.onSubmitBound = (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!this.loading) this.submitBundle(event);
      };
      this.form.addEventListener('submit', this.onSubmitBound, true);
      if (this.button) {
        // Coalesce external app/theme writes; never synchronously observe our own writes.
        this.buttonObserver = new MutationObserver(() => this.scheduleButtonSync());
      }
      this.placeRisk();
      this.syncButton();
      this.scheduleStickyVisibility();
    }

    placeRisk() {
      this.placedRisk?.remove();
      const template = this.querySelector(':scope > [data-jefe-risk]');
      const productForm = this.form?.closest('product-form');
      if (!template || !productForm) return;
      const owner = this.id || this.dataset.formId;
      // A section transition may clone markup; retain just one risk banner per offer.
      this.section.querySelectorAll('[data-jefe-risk-owner]').forEach((node) => {
        if (node.dataset.jefeRiskOwner === owner) node.remove();
      });
      this.placedRisk = template.cloneNode(true);
      this.placedRisk.dataset.jefeRiskOwner = owner;
      this.placedRisk.removeAttribute('data-jefe-risk');
      this.placedRisk.classList.add('jefe-bundle__risk--placed');
      const styles = getComputedStyle(this);
      ['--jefe-text','--jefe-green','--jefe-muted'].forEach((name) => this.placedRisk.style.setProperty(name, styles.getPropertyValue(name)));
      productForm.insertAdjacentElement('afterend', this.placedRisk);
    }

    syncButton() {
      if (!this.button?.isConnected || !this.isConnected) return;
      // The previous implementation watched this button and changed it again in the
      // observer callback. That queued an endless stream of microtasks and froze
      // both the storefront and the theme editor. Suspend observation while writing.
      this.buttonObserver?.disconnect();
      try {
        const total = this.totalBags();
        const tier = this.tier(total);
        const reason = this.blockedReason();
        const disabled = this.loading || Boolean(reason);
        if (this.button.disabled !== disabled) this.button.disabled = disabled;
        const ariaDisabled = disabled ? 'true' : 'false';
        if (this.button.getAttribute('aria-disabled') !== ariaDisabled) {
          this.button.setAttribute('aria-disabled', ariaDisabled);
        }
        const label = this.button.querySelector(':scope > span:not(.loading__spinner)');
        if (!label) return;
        if (total === 0) {
          setText(label, this.config.emptyButtonLabel || 'Select at least 1 bag');
          return;
        }
        if (reason) {
          setText(label, this.config.buttonLabel || 'ADD TO CART');
          return;
        }
        const totalPrice = this.price(this.mode, tier) * total;
        const otpTotal = this.price('onetime', tier) * total;
        const titleText = this.config.buttonLabel || 'ADD TO CART';
        const amountText = this.money(totalPrice);
        const showCompare = this.mode === 'subscription' && otpTotal > totalPrice;
        const compareText = showCompare ? this.money(otpTotal) : '';
        const expectedText = `${titleText} — ${amountText}${showCompare ? ` ${compareText}` : ''}`;
        if (label.textContent === expectedText && label.querySelector('.jefe-atc__total') &&
            Boolean(label.querySelector('.jefe-atc__compare')) === showCompare) return;
        const fragment = document.createDocumentFragment();
        const title = document.createElement('span');
        title.className = 'jefe-atc__label';
        title.textContent = titleText;
        const amount = document.createElement('span');
        amount.className = 'jefe-atc__total';
        amount.textContent = amountText;
        fragment.append(title, document.createTextNode(' — '), amount);
        if (showCompare) {
          const compare = document.createElement('s');
          compare.className = 'jefe-atc__compare';
          compare.textContent = compareText;
          fragment.append(document.createTextNode(' '), compare);
        }
        label.replaceChildren(fragment);
      } finally {
        this.syncStickyButton();
        this.observeButton();
      }
    }

    buildItems() {
      const total = this.totalBags();
      const scope = String(this.config.giftScope || this.dataset.productId || 'jefe');
      const baseProperties = {};
      const gifts = Array.isArray(this.config.gifts) ? this.config.gifts : [];
      if (gifts.length) {
        baseProperties._jefe_gift_scope = scope;
        gifts.forEach((gift) => {
          baseProperties[`_jefe_gift_${gift.slot}_variant`] = String(gift.id);
          baseProperties[`_jefe_gift_${gift.slot}_min`] = String(gift.minimum);
        });
      }
      return this.selectedEntries().map((entry) => {
        const item = { id: Number(entry.id), quantity: entry.quantity };
        const properties = { ...baseProperties };
        if (this.mode === 'subscription') {
          const deliveryDays = this.deliveryDaysForTier(this.tier(total));
          const plan = this.planForVariant(entry.variant, total, deliveryDays);
          if (plan) item.selling_plan = Number(plan.id);

          // Persist both the selected cadence and the complete tier/cadence plan map.
          // Shopify does not automatically change a line's selling plan when cart
          // quantity changes, so the cart controller can re-price without guessing.
          properties._jefe_bundle_scope = scope;
          properties._jefe_tiered_subscription = '1';
          properties._jefe_frequency_days = String(deliveryDays);
          for (let step = 1; step <= 4; step += 1) {
            const daysList = [30, 60];
            daysList.forEach((days) => {
              const tierPlan = this.planForVariant(entry.variant, step, days);
              if (tierPlan?.id) properties[`_jefe_plan_${step}_${days}`] = String(tierPlan.id);
            });
          }
        }
        if (Object.keys(properties).length) item.properties = properties;
        return item;
      });
    }

    async submitBundle(event) {
      const reason = this.blockedReason();
      if (reason) { this.showError(reason); this.syncButton(); return; }
      const items = this.buildItems();
      if (!items.length) return;
      this.loading = true;
      this.showError('');
      this.button?.classList.add('loading');
      this.button?.querySelector('.loading__spinner')?.classList.remove('hidden');
      this.syncButton();
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      if (cart?.setActiveElement) cart.setActiveElement(document.activeElement);
      const sectionIds = cart?.getSectionsToRender ? cart.getSectionsToRender().map((section) => section.id) : [];
      const sectionsUrl = window.location.pathname + window.location.search;
      try {
        let response;
        if (window.JefeGiftCart?.addBundle) {
          response = await window.JefeGiftCart.addBundle(items, sectionIds, sectionsUrl);
        } else {
          const request = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ items, sections: sectionIds, sections_url: sectionsUrl }),
          });
          response = await request.json();
          if (!request.ok || response?.status >= 400 || response?.errors) throw new Error(response?.description || response?.message || 'Unable to add this bundle.');
        }
        if (!cart) {
          window.location.href = window.routes?.cart_url || '/cart';
          return;
        }
        if (response?.sections && cart.renderContents) {
          // A bundle can be the first item added to an empty cart. Dawn's native
          // ProductForm clears <cart-drawer>.is-empty in its finally() handler,
          // but this custom multi-variant bundle form bypasses that handler. If
          // the stale class remains, the theme CSS hides the freshly rendered
          // drawer header, line items and footer, leaving only a blank panel.
          if (cart.matches?.('cart-drawer')) {
            cart.classList.remove('is-empty');
            cart.querySelector('cart-drawer-items')?.classList.remove('is-empty');
          }
          cart.renderContents(response);
          if (cart.matches?.('cart-drawer')) {
            cart.classList.remove('is-empty');
            cart.querySelector('cart-drawer-items')?.classList.remove('is-empty');
          }
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'jefe-bundle', productVariantId: items[0]?.id, cartData: response });
        }
        this.showError('');
      } catch (error) {
        console.error('[JEFE bundle add]', error);
        this.showError(error?.message || 'Unable to add this bundle. Please try again.');
      } finally {
        this.loading = false;
        this.button?.classList.remove('loading');
        this.button?.querySelector('.loading__spinner')?.classList.add('hidden');
        this.syncButton();
        if (typeof CartPerformance !== 'undefined' && CartPerformance?.measureFromEvent && event) {
          try { CartPerformance.measureFromEvent('add:user-action', event); } catch (_) {}
        }
      }
    }
  }

  customElements.define('jefe-product-offer', JefeProductOffer);
})();
